import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, QrCode,
  Users, Sparkles, Zap, Loader2, RefreshCw, Headphones,
  BarChart3, Bot, AlertTriangle, Clock, Circle,
} from "lucide-react";
import { useOnboardingProgress, ONBOARDING_STEPS } from "@/hooks/useOnboardingProgress";
import { events as analyticsEvents } from "@/lib/analytics";
import { SyraLogo } from "@/components/brand/SyraLogo";

const TEMPLATES = [
  { id: "suporte", name: "Suporte", icon: Headphones, desc: "Atendimento e resolução de problemas", color: "text-blue-500" },
  { id: "comunidade", name: "Comunidade", icon: Users, desc: "Engajamento e networking entre membros", color: "text-green-500" },
  { id: "equipe", name: "Equipe", icon: BarChart3, desc: "Produtividade e gestão de projetos", color: "text-orange-500" },
];

interface WhatsAppGroup {
  id: string;
  subject: string;
  size: number;
}

type QrState = "idle" | "generating" | "waiting" | "connected" | "expired" | "error";

const STEP_LABELS = [
  { label: "Boas-vindas", icon: Sparkles },
  { label: "WhatsApp", icon: QrCode },
  { label: "Grupos", icon: Users },
  { label: "Template", icon: Zap },
  { label: "Ativar", icon: CheckCircle2 },
];

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
};

const QR_TIMEOUT_MS = 120_000; // 2 min

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { progressPct, completedSteps, totalSteps, markCheckpoint, progress, isComplete, isLoading: loadingProgress } = useOnboardingProgress();

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [skipGuard, setSkipGuard] = useState(false);

  // WhatsApp state
  const [instanceName, setInstanceName] = useState("");
  const [qrData, setQrData] = useState<string | null>(null);
  const [qrState, setQrState] = useState<QrState>("idle");
  const [qrTimer, setQrTimer] = useState<number | null>(null);

  // Groups state
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Template & activation
  const [selectedTemplate, setSelectedTemplate] = useState("comunidade");
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  // Org ID — fonte única: progress.org_id (vindo do useOnboardingProgress hook).
  // Removida query duplicada que duplicava a leitura de org_members.
  const orgId = progress && (progress as Record<string, unknown>).org_id
    ? ((progress as Record<string, unknown>).org_id as string)
    : null;

  const goTo = (nextStep: number) => {
    setDirection(nextStep > step ? 1 : -1);
    setStep(nextStep);
    analyticsEvents.onboardingStep(STEP_LABELS[nextStep]?.label ?? `step_${nextStep}`);
  };

  // WhatsApp connection with QR timeout
  const handleCreateInstance = async () => {
    const name = instanceName.trim() || `syra-${Date.now()}`;
    setInstanceName(name);
    setQrState("generating");
    setQrData(null);

    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-session", {
        body: { action: "create_instance", instance_name: name },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      const { data: qr, error: qrErr } = await supabase.functions.invoke("whatsapp-session", {
        body: { action: "get_qr", instance_name: name },
      });
      if (qrErr) throw new Error(qrErr.message);

      if (qr?.qr?.base64) {
        setQrData(qr.qr.base64);
      } else if (qr?.qr?.pairingCode) {
        setQrData(qr.qr.pairingCode);
      }

      setQrState("waiting");

      // QR timeout
      const timer = window.setTimeout(() => {
        setQrState("expired");
      }, QR_TIMEOUT_MS);
      setQrTimer(timer);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao conectar";
      toast.error(msg);
      setQrState("error");
    }
  };

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (qrTimer) clearTimeout(qrTimer);
    };
  }, [qrTimer]);

  // Poll connection status
  useEffect(() => {
    if (qrState !== "waiting" || !instanceName) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await supabase.functions.invoke("whatsapp-session", {
          body: { action: "connection_status", instance_name: instanceName },
        });
        if (data?.status?.state === "open" || data?.status?.instance?.state === "open") {
          setQrState("connected");
          if (qrTimer) clearTimeout(qrTimer);
          toast.success("WhatsApp conectado!");
          markCheckpoint.mutate("whatsapp_connected_at");
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [qrState, instanceName, qrTimer, markCheckpoint]);

  // Load groups
  const loadGroups = useCallback(async () => {
    if (!instanceName) return;
    setLoadingGroups(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-session", {
        body: { action: "list_groups", instance_name: instanceName },
      });
      if (error) throw new Error(error.message);
      setGroups(data?.groups || []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao listar grupos";
      toast.error(msg);
    } finally {
      setLoadingGroups(false);
    }
  }, [instanceName]);

  useEffect(() => {
    if (step === 2 && qrState === "connected") loadGroups();
  }, [step, qrState, loadGroups]);

  const toggleGroup = (id: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Activate
  const handleActivate = async () => {
    if (!orgId) { toast.error("Organização não encontrada"); return; }
    setActivating(true);
    try {
      const selectedGroupData = groups.filter((g) => selectedGroups.has(g.id));
      const { data, error } = await supabase.functions.invoke("whatsapp-session", {
        body: {
          action: "activate_groups",
          org_id: orgId,
          instance_name: instanceName,
          groups: selectedGroupData,
          template: selectedTemplate,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      setActivated(true);
      toast.success(`${data?.groups_created?.length || 0} grupo(s) ativado(s)!`);
      markCheckpoint.mutate("agent_configured_at");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao ativar";
      toast.error(msg);
    } finally {
      setActivating(false);
    }
  };

  const handleFinish = () => navigate("/dashboard");

  // B4: se onboarding já está completo e o usuário não pediu para revisitar, mostra tela de "tudo pronto"
  // em vez de remarcar passos como pendentes.
  if (!loadingProgress && isComplete && !skipGuard) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold font-headline">Onboarding concluído</h2>
              <p className="text-sm text-muted-foreground">
                Você já completou todos os {totalSteps} passos de configuração. Pode ir direto para o dashboard.
              </p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button onClick={handleFinish} className="gradient-primary text-primary-foreground">
                Ir para o dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={() => setSkipGuard(true)}>
                Revisitar passos
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-72 h-72 rounded-full bg-primary/8 blur-3xl" />
      </div>

      {/* Logo */}
      <div className="flex items-center gap-2 mb-4">
        <SyraLogo size={32} variant="lockup" />
      </div>

      {/* Persistent progress bar */}
      <div className="w-full max-w-xl mb-4 space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Setup: {completedSteps}/{totalSteps} checkpoints</span>
          <span>{progressPct}%</span>
        </div>
        <Progress value={progressPct} className="h-2" />
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {STEP_LABELS.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              onClick={() => goTo(i)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                i < step ? "bg-primary text-primary-foreground" :
                i === step ? "bg-primary text-primary-foreground ring-4 ring-primary/20" :
                "bg-muted text-muted-foreground"
              }`}
            >
              {i < step ? <CheckCircle2 className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
            </button>
            {i < STEP_LABELS.length - 1 && (
              <div className={`w-8 h-0.5 ${i < step ? "bg-primary" : "bg-border"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="w-full max-w-xl relative" style={{ minHeight: 400 }}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {/* STEP 0: Welcome */}
            {step === 0 && (
              <Card>
                <CardContent className="p-8 text-center space-y-6">
                  <div className="w-20 h-20 rounded-3xl gradient-primary flex items-center justify-center mx-auto">
                    <Bot className="h-10 w-10 text-primary-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-2xl font-bold font-headline">Bem-vindo ao SYRA! 🎉</h2>
                    <p className="text-muted-foreground">
                      Vamos configurar sua IA em poucos passos. Seu progresso é salvo automaticamente.
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {[
                      { icon: QrCode, label: "Conecte WhatsApp" },
                      { icon: Users, label: "Selecione grupos" },
                      { icon: Zap, label: "Ative a IA" },
                    ].map((item, i) => (
                      <div key={i} className="space-y-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mx-auto">
                          <item.icon className="h-5 w-5 text-primary" />
                        </div>
                        <p className="text-xs text-muted-foreground">{item.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => goTo(1)} className="gradient-primary text-primary-foreground px-8">
                      Começar <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button variant="ghost" onClick={handleFinish} className="text-muted-foreground">
                      Pular por agora
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 1: WhatsApp QR */}
            {step === 1 && (
              <Card>
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-xl font-bold font-headline">Conecte seu WhatsApp</h2>
                    <p className="text-sm text-muted-foreground">
                      Dê um nome para a instância e escaneie o QR code.
                    </p>
                  </div>

                  {qrState === "idle" && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Nome da instância</label>
                        <Input
                          value={instanceName}
                          onChange={(e) => setInstanceName(e.target.value)}
                          placeholder="Ex: minha-empresa"
                          className="h-11"
                        />
                      </div>
                      <Button onClick={handleCreateInstance} className="w-full gradient-primary text-primary-foreground">
                        <QrCode className="h-4 w-4 mr-2" /> Gerar QR Code
                      </Button>
                    </div>
                  )}

                  {qrState === "generating" && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      <span className="ml-2 text-sm text-muted-foreground">Gerando QR Code...</span>
                    </div>
                  )}

                  {qrState === "waiting" && qrData && (
                    <div className="text-center space-y-4">
                      <div className="bg-white p-4 rounded-xl inline-block mx-auto">
                        {qrData.startsWith("data:") || qrData.length > 100 ? (
                          <img
                            src={qrData.startsWith("data:") ? qrData : `data:image/png;base64,${qrData}`}
                            alt="QR Code"
                            className="w-48 h-48"
                          />
                        ) : (
                          <div className="w-48 h-48 flex items-center justify-center">
                            <div className="text-center">
                              <p className="text-sm text-gray-600 mb-2">Código de pareamento:</p>
                              <p className="text-2xl font-mono font-bold text-gray-900">{qrData}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Aguardando conexão… (expira em 2 min)
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleCreateInstance}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Gerar novo QR
                      </Button>
                    </div>
                  )}

                  {qrState === "expired" && (
                    <div className="text-center space-y-4">
                      <Clock className="h-12 w-12 text-warning mx-auto" />
                      <p className="text-sm font-medium text-warning">QR Code expirado</p>
                      <p className="text-xs text-muted-foreground">O código expirou. Gere um novo para continuar.</p>
                      <Button onClick={handleCreateInstance} variant="outline">
                        <RefreshCw className="h-4 w-4 mr-1" /> Regenerar QR Code
                      </Button>
                    </div>
                  )}

                  {qrState === "error" && (
                    <div className="text-center space-y-4">
                      <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
                      <p className="text-sm font-medium text-destructive">Erro na conexão</p>
                      <p className="text-xs text-muted-foreground">Não foi possível criar a instância. Verifique a configuração.</p>
                      <div className="flex gap-2 justify-center">
                        <Button onClick={handleCreateInstance} variant="outline">
                          <RefreshCw className="h-4 w-4 mr-1" /> Tentar novamente
                        </Button>
                        <Button variant="ghost" onClick={() => navigate("/support")}>
                          Abrir suporte
                        </Button>
                      </div>
                    </div>
                  )}

                  {qrState === "connected" && (
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                        <CheckCircle2 className="h-8 w-8 text-primary" />
                      </div>
                      <p className="font-semibold text-primary">WhatsApp conectado com sucesso!</p>
                    </div>
                  )}

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={() => goTo(0)}>
                      <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
                    </Button>
                    <Button onClick={() => goTo(2)} disabled={qrState === "generating"}>
                      {qrState === "connected" ? "Próximo" : "Pular"} <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 2: Select groups */}
            {step === 2 && (
              <Card>
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-xl font-bold font-headline">Selecione seus grupos</h2>
                    <p className="text-sm text-muted-foreground">Escolha os grupos onde a SYRA vai atuar.</p>
                  </div>

                  {loadingGroups ? (
                    <div className="space-y-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full rounded-lg" />
                      ))}
                    </div>
                  ) : groups.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                      {groups.map((g) => (
                        <div
                          key={g.id}
                          onClick={() => toggleGroup(g.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedGroups.has(g.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                          }`}
                        >
                          <Checkbox checked={selectedGroups.has(g.id)} />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{g.subject}</p>
                            <p className="text-xs text-muted-foreground">{g.size} membros</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 space-y-3">
                      <Users className="h-10 w-10 text-muted-foreground/30 mx-auto" />
                      <p className="text-sm text-muted-foreground">
                        {qrState === "connected" ? "Nenhum grupo encontrado." : "Conecte o WhatsApp primeiro."}
                      </p>
                      {qrState === "connected" && (
                        <Button variant="outline" size="sm" onClick={loadGroups}>
                          <RefreshCw className="h-4 w-4 mr-1" /> Recarregar
                        </Button>
                      )}
                    </div>
                  )}

                  {selectedGroups.size > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {selectedGroups.size} grupo(s) selecionado(s)
                    </Badge>
                  )}

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={() => goTo(1)}>
                      <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
                    </Button>
                    <Button onClick={() => goTo(3)}>
                      Próximo <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 3: Template */}
            {step === 3 && (
              <Card>
                <CardContent className="p-8 space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-xl font-bold font-headline">Escolha um template</h2>
                    <p className="text-sm text-muted-foreground">O template define o comportamento padrão do agente.</p>
                  </div>

                  <div className="grid gap-3">
                    {TEMPLATES.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setSelectedTemplate(t.id)}
                        className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedTemplate === t.id ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30"
                        }`}
                      >
                        <t.icon className={`h-6 w-6 ${t.color}`} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{t.desc}</p>
                        </div>
                        {selectedTemplate === t.id && <CheckCircle2 className="h-5 w-5 text-primary" />}
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={() => goTo(2)}>
                      <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
                    </Button>
                    <Button onClick={() => goTo(4)}>
                      Próximo <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 4: Activate */}
            {step === 4 && (
              <Card>
                <CardContent className="p-8 text-center space-y-6">
                  {!activated ? (
                    <>
                      <div className="w-20 h-20 rounded-3xl gradient-primary flex items-center justify-center mx-auto">
                        <Zap className="h-10 w-10 text-primary-foreground" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-2xl font-bold font-headline">Tudo pronto!</h2>
                        <p className="text-muted-foreground">
                          {selectedGroups.size > 0
                            ? `${selectedGroups.size} grupo(s) selecionado(s) com template "${selectedTemplate}".`
                            : "Nenhum grupo selecionado. Você pode configurar depois."}
                        </p>
                      </div>
                      <div className="flex gap-3 justify-center">
                        <Button
                          onClick={handleActivate}
                          disabled={activating || selectedGroups.size === 0}
                          className="gradient-primary text-primary-foreground px-8"
                        >
                          {activating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                          Ativar SYRA
                        </Button>
                        <Button variant="ghost" onClick={handleFinish}>
                          Configurar depois
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto">
                        <CheckCircle2 className="h-10 w-10 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-2xl font-bold font-headline">SYRA ativada! 🚀</h2>
                        <p className="text-muted-foreground">
                          Seu agente já está operando nos grupos selecionados.
                        </p>
                      </div>
                      <Button onClick={handleFinish} className="gradient-primary text-primary-foreground px-8">
                        Ir para o Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </>
                  )}

                  {!activated && (
                    <div className="flex justify-start pt-2">
                      <Button variant="ghost" onClick={() => goTo(3)}>
                        <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Persistent onboarding checkpoints */}
      <div className="w-full max-w-xl mt-6">
        <div className="flex flex-wrap gap-2 justify-center">
          {ONBOARDING_STEPS.map((s) => {
            const isDone = progress && (progress as Record<string, unknown>)[s.key];
            return (
              <Badge
                key={s.key}
                variant={isDone ? "default" : "outline"}
                className="text-xs gap-1"
              >
                {isDone ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                {s.label}
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
}
